import type { ComponentProps } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

// Semantic status kinds. Every domain-specific status (open/closed,
// active/inactive, complete/failed/expired/pending) collapses to one of
// these visual variants — callers pass the raw domain value and pick which
// variant reads best for their case.
export type StatusVariant = 'success' | 'warning' | 'danger' | 'info' | 'neutral';

interface Props extends Omit<ComponentProps<typeof Badge>, 'variant' | 'children'> {
  variant: StatusVariant;
  label: string;
}

const VARIANT_CLASS: Record<StatusVariant, string> = {
  success: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100/80',
  warning: 'bg-amber-100 text-amber-800 hover:bg-amber-100/80',
  danger: 'bg-red-100 text-red-800 hover:bg-red-100/80',
  info: 'bg-blue-100 text-blue-800 hover:bg-blue-100/80',
  neutral: 'bg-slate-100 text-slate-700 hover:bg-slate-100/80',
};

// Consistent status pill. Replaces the per-call `bg-*-100 text-*-700`
// conditionals scattered across issue/upload/customer components (see
// UI-AUDIT §5). The palette classes are still tailwind semantic colours —
// they will move to tokens with the broader colour rethink; the point of
// this component is single-source-of-truth NOW so future swaps are one file.
export function StatusBadge({ variant, label, className, ...rest }: Props) {
  return (
    <Badge
      data-testid="status-badge"
      data-variant={variant}
      className={cn(VARIANT_CLASS[variant], className)}
      {...rest}
    >
      {label}
    </Badge>
  );
}
